import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from "recharts";

const TABS = [
  { key: "body", label: "Weight & waist" },
  { key: "bmi", label: "BMI" },
  { key: "whtr", label: "Waist-to-height" },
];

function ChartBody({ tab, rows }) {
  if (tab === "body") {
    return (
      <LineChart data={rows}>
        <CartesianGrid stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
        <YAxis yAxisId="w" tick={{ fontSize: 10 }} width={34} domain={["auto", "auto"]} />
        <YAxis yAxisId="c" orientation="right" tick={{ fontSize: 10 }} width={34} domain={["auto", "auto"]} />
        <Tooltip contentStyle={{ fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line yAxisId="w" dataKey="weight_kg" name="Weight (kg)" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} connectNulls />
        <Line yAxisId="c" dataKey="waist_cm" name="Waist (cm)" stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} connectNulls />
      </LineChart>
    );
  }
  const isBmi = tab === "bmi";
  return (
    <LineChart data={rows}>
      <CartesianGrid stroke="#f1f5f9" vertical={false} />
      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
      <YAxis tick={{ fontSize: 10 }} width={34} domain={isBmi ? [15, "auto"] : [0.3, "auto"]} />
      <Tooltip contentStyle={{ fontSize: 12 }} />
      {(isBmi ? [18.5, 25, 30] : [0.5, 0.6]).map((y) => (
        <ReferenceLine key={y} y={y} stroke={y === 18.5 || y === 0.5 ? "#10b981" : "#f43f5e"} strokeDasharray="4 3" />
      ))}
      <Line dataKey={tab} name={isBmi ? "BMI" : "Waist-to-height"} stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} connectNulls />
    </LineChart>
  );
}

export function useMeasurementPoints(clientId, canonicalClientId, enabled = true) {
  const [points, setPoints] = useState(null);
  useEffect(() => {
    if (!enabled || (!clientId && !canonicalClientId)) return;
    const params = {};
    if (clientId) params.client_id = clientId;
    if (canonicalClientId) params.canonical_client_id = canonicalClientId;
    api.get("/assessments/measurement-trend", { params })
      .then((r) => setPoints(r.data?.points || []))
      .catch(() => setPoints([]));
  }, [clientId, canonicalClientId, enabled]);
  return points;
}

export function MeasurementTrend({ clientId, canonicalClientId, current, points: given }) {
  const fetched = useMeasurementPoints(clientId, canonicalClientId, given === undefined);
  const points = given === undefined ? fetched : given;
  const [tab, setTab] = useState("body");

  const rows = useMemo(() => {
    const list = (points || []).map((p) => (current && p.assessment_id === current.assessment_id ? { ...p, ...current } : p));
    if (current && !list.some((p) => p.assessment_id === current.assessment_id) && (current.weight_kg || current.waist_cm)) {
      list.push(current);
    }
    return list.map((p) => ({ ...p, label: p.date ? new Date(p.date).toLocaleDateString() : "—" }));
  }, [points, current]);

  if (!points || rows.length === 0) return null;

  return (
    <div className="rounded border border-zinc-200 p-3 space-y-2" data-testid="measurement-trend">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5" /> Measurement trend · {rows.length} assessment{rows.length === 1 ? "" : "s"}
        </div>
        <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden">
          {TABS.map((t, i) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-2.5 h-7 text-xs transition-colors ${tab === t.key ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"} ${i ? "border-l border-zinc-300" : ""}`}
              data-testid={`measurement-trend-tab-${t.key}`}
            >{t.label}</button>
          ))}
        </div>
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 200 }}>
          <ChartBody tab={tab} rows={rows} />
        </ResponsiveContainer>
      </div>
    </div>
  );
}
